import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
  FaEye,
} from "react-icons/fa";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, createSearchParams } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/lubeConsumptionAlert";

const LubeConsumptionAlert = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [showFilters, setShowFilters] = useState(true);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // own/hire (map to backend: O / H)
  const [vehicleType, setVehicleType] = useState("own"); // "own" | "hire"

  // dropdown data from init api
  const [branches, setBranches] = useState([]);
  const [itemCategories, setItemCategories] = useState([]);
  const [itemNames, setItemNames] = useState([]);
  const [vehicleMainTypes, setVehicleMainTypes] = useState([]);
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState([]);

  // table data from list api
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ backend-matching headers
  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  const [filters, setFilters] = useState({
    branch: sessionBranchType !== "HO" ? sessionBranchId : "",
    month: "September",
    year: "2025",
    vehicleRegNo: "",
    vehicleName: "",
    vehicleCode: "",
    vehicleMainType: "",
    itemCategory: "",
    itemName: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const fetchInit = async () => {
    try {
      setLoadingInit(true);

      const res = await axios.get(`${API_BASE}/init`, {
        headers: apiHeaders,
      });

      if (!res.data?.success) {
        throw new Error(res.data?.message || "Init failed");
      }

      const branchList = res.data.branches || [];
      const itemNameList = res.data.itemNames || [];
      const monthList = res.data.months || [];
      const yearList = res.data.years || [];
      const vehicleTypeList = res.data.vehicleMainTypes || [];
      const lockedCategory = res.data.itemCategory || null;
      const userLock = res.data.userLock || {};

      setBranches(branchList);
      setItemNames(itemNameList);
      setVehicleMainTypes(vehicleTypeList);
      setMonths(monthList);
      setYears(yearList);

      if (lockedCategory) {
        setItemCategories([
          {
            label: lockedCategory.name || "FUELS AND LUBRICANTS",
            value: lockedCategory.id || "Select",
          },
        ]);
      } else {
        setItemCategories([]);
      }

      setFilters((prev) => ({
        ...prev,
        branch:
          userLock.branchLocked && userLock.branchValue
            ? userLock.branchValue
            : prev.branch,
        itemCategory: lockedCategory?.id || prev.itemCategory || "Select",
      }));
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to load filters");
    } finally {
      setLoadingInit(false);
    }
  };

  const fetchList = async ({ page = currentPage, pageSize = itemsPerPage } = {}) => {
    try {
      setLoadingList(true);

      const vehtype = vehicleType === "own" ? "O" : "H";

      const params = {
        page,
        pageSize,
        branchId:
          filters.branch || (sessionBranchType !== "HO" ? sessionBranchId : "Select"),
        monthText: filters.month,
        year: filters.year,
        vehicleRegNo: filters.vehicleRegNo || "",
        vehicleName: filters.vehicleName || "",
        vehicleCode: filters.vehicleCode || "",
        vehicleMainType: filters.vehicleMainType || "Select",
        itemCategoryId: filters.itemCategory || "Select",
        itemId: filters.itemName || "Select",
        vehtype,
      };

      const res = await axios.get(`${API_BASE}/list`, {
        params,
        headers: apiHeaders,
      });

      if (!res.data?.success) {
        throw new Error(res.data?.message || "List fetch failed");
      }

      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      setCurrentPage(res.data.page || page);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to load alerts");
      setRows([]);
      setTotal(0);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchInit();
  }, []);

  useEffect(() => {
    fetchList({ page: currentPage, pageSize: itemsPerPage });
  }, [currentPage]);

  const handleSearch = async () => {
    setCurrentPage(1);
    await fetchList({ page: 1, pageSize: itemsPerPage });
    toast.success("Search completed");
  };

  const handleViewAll = async () => {
    setFilters((prev) => ({
      ...prev,
      branch: sessionBranchType !== "HO" ? sessionBranchId : "",
      vehicleRegNo: "",
      vehicleName: "",
      vehicleCode: "",
      vehicleMainType: "",
      itemName: "",
    }));
    setCurrentPage(1);
    await fetchList({ page: 1, pageSize: itemsPerPage });
    toast.info("Showing all alerts");
  };

  const handleClearFilters = () => {
    setFilters({
      branch: sessionBranchType !== "HO" ? sessionBranchId : "",
      month: "September",
      year: "2025",
      vehicleRegNo: "",
      vehicleName: "",
      vehicleCode: "",
      vehicleMainType: "",
      itemCategory: itemCategories?.[0]?.value || "Select",
      itemName: "",
    });
    toast.info("Filters cleared!");
  };

  const handlePrintReport = () => {
    const vehtype = vehicleType === "own" ? "O" : "H";

    const query = {
      vehtype,
      branchId:
        filters.branch || (sessionBranchType !== "HO" ? sessionBranchId : "Select"),
      monthText: filters.month,
      year: filters.year,
      vehicleRegNo: filters.vehicleRegNo || "",
      vehicleName: filters.vehicleName || "",
      vehicleCode: filters.vehicleCode || "",
      vehicleMainType: filters.vehicleMainType || "Select",
      itemCategoryId: filters.itemCategory || "Select",
      itemId: filters.itemName || "Select",
      page: "1",
      pageSize: "10000",
    };

    navigate({
      pathname: "/vehicle/LubeConsumptionAlert_Print",
      search: `?${createSearchParams(query).toString()}`,
    });
  };

  const totalPages = Math.ceil(total / itemsPerPage);
  const indexOfFirstItem = total === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const indexOfLastItem = Math.min(currentPage * itemsPerPage, total);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const radioClass = `mr-2 ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const getStatusClass = (status) => {
    switch (status) {
      case "Critical":
        return "bg-red-100 text-red-800";
      case "Warning":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-green-100 text-green-800";
    }
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">LUBE CONSUMPTION ALERT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Lube Consumption Alert
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Alerts
              </span>
              {(loadingInit || loadingList) && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" +
                      (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                      (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handlePrintReport}
                className="flex items-center gap-2 bg-black hover:bg-gray-900 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
              >
                <FaPrint />
                Print Report
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="mb-6">
                <label className={labelClass}>Vehicle Type:</label>
                <div className="flex gap-6">
                  <label className={radioClass}>
                    <input
                      type="radio"
                      name="vehicleType"
                      className="mr-2"
                      checked={vehicleType === "own"}
                      onChange={() => {
                        setVehicleType("own");
                        setCurrentPage(1);
                      }}
                    />
                    Own Vehicle
                  </label>
                  <label className={radioClass}>
                    <input
                      type="radio"
                      name="vehicleType"
                      className="mr-2"
                      checked={vehicleType === "hire"}
                      onChange={() => {
                        setVehicleType("hire");
                        setCurrentPage(1);
                      }}
                    />
                    Hire Vehicle
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={sessionBranchType !== "HO"}
                  >
                    <option value="">Select</option>
                    {branches.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Month</label>
                  <select
                    name="month"
                    value={filters.month}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    {(months.length
                      ? months.map((m) => m.text || m.label)
                      : [
                          "January",
                          "February",
                          "March",
                          "April",
                          "May",
                          "June",
                          "July",
                          "August",
                          "September",
                          "October",
                          "November",
                          "December",
                        ]).map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Year</label>
                  <select
                    name="year"
                    value={filters.year}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    {(years.length ? years : ["2025", "2026"]).map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Registration Number</label>
                  <input
                    type="text"
                    name="vehicleRegNo"
                    value={filters.vehicleRegNo}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter registration number"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter vehicle name"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter vehicle code"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    name="vehicleMainType"
                    value={filters.vehicleMainType}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {vehicleMainTypes.map((v) => (
                      <option key={v.value} value={v.value}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Item Category</label>
                  <select
                    name="itemCategory"
                    value={filters.itemCategory}
                    onChange={handleChange}
                    className={inputClass}
                    disabled
                  >
                    {itemCategories.length ? (
                      itemCategories.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))
                    ) : (
                      <option value="Select">FUELS AND LUBRICANTS</option>
                    )}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Item Name</label>
                  <select
                    name="itemName"
                    value={filters.itemName}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {itemNames.map((i) => (
                      <option key={i.value} value={i.value}>
                        {i.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleClearFilters}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                >
                  Clear All Filters
                </button>

                <div className="flex gap-4">
                  <button
                    onClick={handleViewAll}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                    disabled={loadingList}
                  >
                    <FaEye />
                    View All
                  </button>

                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                    disabled={loadingList}
                  >
                    <FaSearch />
                    Search Alerts
                  </button>
                </div>
              </div>
            </div>
          )}

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  No Alerts Found
                </h3>
                <p
                  className={`mb-6 max-w-sm mx-auto ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Try adjusting your search criteria or click "View All" to see all alerts.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">Branch</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Reg No</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Name</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Code</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Main Type</th>
                      <th className="px-4 py-4 text-center font-semibold">Item Category</th>
                      <th className="px-4 py-4 text-center font-semibold">Item Name</th>
                      <th className="px-4 py-4 text-center font-semibold">Current Level</th>
                      <th className="px-4 py-4 text-center font-semibold">Threshold</th>
                      <th className="px-4 py-4 text-center font-semibold">Status</th>
                      <th className="px-4 py-4 text-center font-semibold">Last Refill</th>
                      <th className="px-4 py-4 text-center font-semibold">Consumption Rate</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((item, index) => (
                      <tr
                        key={item.id || `${item.vehicleRegNo}-${item.itemName}-${item.branch}-${index}`}
                        className={`${
                          isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-50"
                        } transition-colors duration-200`}
                      >
                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.branch}
                        </td>
                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.vehicleRegNo}
                        </td>
                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.vehicleName}
                        </td>
                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.vehicleCode}
                        </td>
                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.vehicleMainType}
                        </td>
                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.itemCategory}
                        </td>
                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.itemName}
                        </td>
                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.currentLevel}
                        </td>
                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.threshold}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusClass(
                              item.status
                            )}`}
                          >
                            {item.status}
                          </span>
                        </td>
                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.lastRefill}
                        </td>
                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.consumptionRate}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {total > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
              <div className={`text-sm mb-4 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {indexOfFirstItem} to {indexOfLastItem} of {total} entries
              </div>

              <nav className="flex gap-1">
                <button
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === 1
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Previous
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => paginate(pageNum)}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === pageNum
                          ? `${buttonClass} text-white`
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === totalPages
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default LubeConsumptionAlert;